import { useContext, useState, useCallback, useEffect, useRef, useMemo, } from 'react';
import toLower from 'lodash/toLower';
import debounce from 'lodash/debounce';
import { keyEventToHotkey, DropdownHotkey } from '../../hotkeys';
import { DropdownStateContext } from '../../DropdownStateManager';
import { mapDropdownItemsToIndexes } from './utils';
import { StandardMenuType } from './types';
import StandardMenuContext from './context';
export var NavigationDirection;
(function (NavigationDirection) {
    NavigationDirection["UP"] = "up";
    NavigationDirection["DOWN"] = "down";
})(NavigationDirection || (NavigationDirection = {}));
/** For use with components that make use of our `DropdownStateContext` and have an element with `role="menu"`  */
export function useMenuHotkeys() {
    var closeDropdown = useContext(DropdownStateContext).closeDropdown;
    var menuType = useContext(StandardMenuContext).menuType;
    var handleGlobalHotkeys = useCallback(function (e) {
        switch (keyEventToHotkey(e)) {
            case DropdownHotkey.TAB:
                if (menuType === StandardMenuType.DROPDOWN)
                    closeDropdown();
                break;
            case DropdownHotkey.ESCAPE:
                closeDropdown();
                e.preventDefault();
                e.stopPropagation();
                break;
            default:
                break;
        }
    }, [closeDropdown, menuType]);
    return handleGlobalHotkeys;
}
/**
 * Stores a search phrase that periodically resets. To be used when jumping focus to a menu item based on the provided `searchPhrase`
 *
 * e.g.
 * An item is focused and a user starts typing `m`
 * We want to store that `m` here for a set period of time
 */
export function useMenuSearch() {
    var _a = useState(''), searchPhrase = _a[0], setSearchPhrase = _a[1];
    // Because we clear the search after a few seconds, we need to keep track of the result
    // of the debounced function call and clean up if this unmounts.
    var debouncedClearSearchRef = useRef();
    var debouncedClearSearchPhrase = useMemo(function () {
        return debounce(function () {
            setSearchPhrase('');
        }, 1000);
    }, [setSearchPhrase]);
    var pushLetter = useCallback(function (letter) {
        setSearchPhrase("".concat(searchPhrase).concat(letter));
        // Return the debounced result in case we need to cancel
        debouncedClearSearchRef.current = debouncedClearSearchPhrase();
    }, [searchPhrase, setSearchPhrase, debouncedClearSearchPhrase]);
    // Make sure we clean up the debounced function if it's hanging around during unmount
    useEffect(function () { var _a; return (_a = debouncedClearSearchRef.current) === null || _a === void 0 ? void 0 : _a.cancel; }, [
        debouncedClearSearchRef,
    ]);
    return {
        searchPhrase: searchPhrase,
        pushLetter: pushLetter,
    };
}
/**
 * For use with components that make use of our `DropdownStateContext` and have an element with `role="menuitem"`
 *
 * Focus management should be handled by the consumer based on the provided `currentIndex`.
 *
 * @param itemCount How many items are we navigating through
 * @param onPassStart What should the behaviour be when we press `DropdownHotkey.ARROW_UP` while at the start of the list? Default behaviour is to wrap around to the bottom.
 * @param onPassEnd What should the behaviour be when we press `DropdownHotkey.ARROW_DOWN` while at the end of the list? Default behaviour is to wrap around to the top.
 * @param onSearch What should the behaviour be when we type alphanumeric characters while an item is focused?
 */
export function useMenuItemHotkeys(itemCount, _a) {
    var _b = _a === void 0 ? {} : _a, onPassStart = _b.onPassStart, onPassEnd = _b.onPassEnd, onSearch = _b.onSearch, onPressHome = _b.onPressHome, onPressEnd = _b.onPressEnd, _c = _b.initialIndex, initialIndex = _c === void 0 ? null : _c;
    var closeDropdown = useContext(DropdownStateContext).closeDropdown;
    var _d = useState(initialIndex), currentIndex = _d[0], setCurrentIndex = _d[1];
    var _e = useState(null), navigationDirection = _e[0], setNavigationDirection = _e[1];
    var handleGlobalHotkeys = useMenuHotkeys();
    var handleHotkeys = useCallback(function (e) {
        // By default, wrap around to the end if a user navigates past the first item
        var handlePassStart = onPassStart
            ? onPassStart
            : function () { return setCurrentIndex(itemCount - 1); };
        // By default, wrap around to the start if a user navigates past the last item
        var handlePassEnd = onPassEnd ? onPassEnd : function () { return setCurrentIndex(0); };
        var handlePressHome = onPressHome
            ? onPressHome
            : function () { return setCurrentIndex(0); };
        var handlePressEnd = onPressEnd
            ? onPressEnd
            : function () { return setCurrentIndex(itemCount - 1); };
        if (currentIndex !== null) {
            switch (keyEventToHotkey(e)) {
                case DropdownHotkey.ARROW_UP:
                    setNavigationDirection(NavigationDirection.UP);
                    if (currentIndex > 0) {
                        setCurrentIndex(currentIndex - 1);
                    }
                    else {
                        handlePassStart();
                    }
                    e.preventDefault();
                    break;
                case DropdownHotkey.ARROW_DOWN:
                    setNavigationDirection(NavigationDirection.DOWN);
                    if (currentIndex < itemCount - 1) {
                        setCurrentIndex(currentIndex + 1);
                    }
                    else {
                        handlePassEnd();
                    }
                    e.preventDefault();
                    break;
                case DropdownHotkey.HOME:
                    handlePressHome();
                    setNavigationDirection(NavigationDirection.DOWN);
                    e.preventDefault();
                    break;
                case DropdownHotkey.END:
                    handlePressEnd();
                    setNavigationDirection(NavigationDirection.UP);
                    e.preventDefault();
                    break;
                case DropdownHotkey.SEARCH:
                    if (onSearch) {
                        onSearch(toLower(e.key));
                    }
                    break;
                default:
                    break;
            }
        }
        else {
            switch (keyEventToHotkey(e)) {
                case DropdownHotkey.HOME:
                    setNavigationDirection(NavigationDirection.DOWN);
                    setCurrentIndex(0);
                    e.preventDefault();
                    break;
                case DropdownHotkey.ARROW_UP:
                    setNavigationDirection(NavigationDirection.UP);
                    setCurrentIndex(0);
                    e.preventDefault();
                    break;
                case DropdownHotkey.END:
                    setNavigationDirection(NavigationDirection.UP);
                    setCurrentIndex(itemCount - 1);
                    e.preventDefault();
                    break;
                case DropdownHotkey.ARROW_DOWN:
                    setNavigationDirection(NavigationDirection.DOWN);
                    setCurrentIndex(itemCount - 1);
                    e.preventDefault();
                    break;
                case DropdownHotkey.SEARCH:
                    if (onSearch) {
                        onSearch(toLower(e.key));
                    }
                    break;
                case DropdownHotkey.SPACE:
                    closeDropdown();
                    break;
                default:
                    break;
            }
        }
        handleGlobalHotkeys(e);
    }, [
        currentIndex,
        setCurrentIndex,
        itemCount,
        handleGlobalHotkeys,
        onSearch,
        closeDropdown,
        onPassEnd,
        onPassStart,
        onPressEnd,
        onPressHome,
    ]);
    return {
        handleKeyboardEvents: handleHotkeys,
        currentIndex: currentIndex,
        setCurrentIndex: setCurrentIndex,
        goToNextIndex: function () {
            if (currentIndex !== null) {
                setNavigationDirection(NavigationDirection.DOWN);
                setCurrentIndex(currentIndex < itemCount - 1 ? currentIndex + 1 : 0);
            }
        },
        goToPreceedingIndex: function () {
            if (currentIndex !== null) {
                setNavigationDirection(NavigationDirection.UP);
                setCurrentIndex(currentIndex > 0 ? currentIndex - 1 : itemCount - 1);
            }
        },
        navigationDirection: navigationDirection,
        setNavigationDirection: setNavigationDirection,
    };
}
/**
 * Provides keyboard support to our StandardMenu components.
 *
 * Modeled after the keyboard UX of a native `<select>` tag.
 */
export function useStandardMenuKeyboardSupport(items) {
    var menuType = useContext(StandardMenuContext).menuType;
    var _a = useContext(DropdownStateContext), isOpen = _a.isOpen, firstKeyInteraction = _a.firstKeyInteraction;
    var itemIndexesByLabels = mapDropdownItemsToIndexes(items);
    var _b = useMenuSearch(), searchPhrase = _b.searchPhrase, pushLetter = _b.pushLetter;
    var _c = useMenuItemHotkeys(items.length, { onSearch: pushLetter }), handleKeyboardEvents = _c.handleKeyboardEvents, currentIndex = _c.currentIndex, setCurrentIndex = _c.setCurrentIndex, goToNextIndex = _c.goToNextIndex, goToPreceedingIndex = _c.goToPreceedingIndex, navigationDirection = _c.navigationDirection, setNavigationDirection = _c.setNavigationDirection;
    var _d = useState(null), groupStartingItemIndex = _d[0], setGroupStartingItemIndex = _d[1];
    // Make sure we set the correct starting index based on what we opened the dropdown with.
    useEffect(function () {
        if (menuType === StandardMenuType.EMBEDDED) {
            return;
        }
        if (isOpen && firstKeyInteraction) {
            switch (firstKeyInteraction) {
                case DropdownHotkey.END:
                case DropdownHotkey.ARROW_UP:
                    setNavigationDirection(NavigationDirection.UP);
                    setCurrentIndex(items.length - 1);
                    setGroupStartingItemIndex(null);
                    break;
                case DropdownHotkey.SPACE:
                case DropdownHotkey.ENTER:
                case DropdownHotkey.HOME:
                case DropdownHotkey.ARROW_DOWN:
                    setNavigationDirection(NavigationDirection.DOWN);
                    setCurrentIndex(0);
                    setGroupStartingItemIndex(null);
                    break;
                default:
                    break;
            }
        }
        else {
            setCurrentIndex(null);
        }
    }, [
        isOpen,
        firstKeyInteraction,
        setCurrentIndex,
        items.length,
        setNavigationDirection,
        menuType,
    ]);
    // Handle setting the current focus based on the searchPhrase
    useEffect(function () {
        if (isOpen && searchPhrase) {
            var searchRegex_1 = new RegExp("^".concat(searchPhrase));
            var matchedLabel = Object.keys(itemIndexesByLabels).find(function (value) {
                return searchRegex_1.test(toLower(value));
            });
            if (matchedLabel) {
                var _a = itemIndexesByLabels[matchedLabel], index = _a.index, innerIndex = _a.innerIndex;
                setCurrentIndex(index);
                setGroupStartingItemIndex(innerIndex);
            }
        }
    }, [isOpen, searchPhrase, setCurrentIndex, itemIndexesByLabels]);
    // Handle setting the index on open if an item is already selected
    // PART2 TODO: Refine this so that it works more consistently
    useEffect(function () {
        var matchedItem = Object.values(itemIndexesByLabels).find(function (item) { return item.selected; });
        if (matchedItem && currentIndex === null) {
            var index = matchedItem.index, innerIndex = matchedItem.innerIndex;
            setCurrentIndex(index);
            setGroupStartingItemIndex(innerIndex);
        }
    }, [
        isOpen,
        itemIndexesByLabels,
        setCurrentIndex,
        setGroupStartingItemIndex,
        currentIndex,
    ]);
    return {
        /** The index of the item that is currently focused */
        currentItemIndex: currentIndex,
        /** The index of an group entry inside a group that is currently focused. Will be null for any item that isn't a group */
        groupStartingItemIndex: groupStartingItemIndex,
        /**
         * A callback to be added to the currently focused item's onKeyDown function.
         * Contains code that handles navigating to other items or closing the menu
         */
        handleKeyboardEvents: handleKeyboardEvents,
        /** Sets currentItemIndex to the preceeding item's index, possibly wrapping around to the end of the list of items. */
        focusPreceedingItem: goToPreceedingIndex,
        /** Sets currentItemIndex to the next item's index, possibly wrapping around to the start of the list of items */
        focusNextItem: goToNextIndex,
        /** Sets currentItemIndex to the first item's index */
        focusFirstItem: function () {
            setCurrentIndex(0);
            setGroupStartingItemIndex(null);
            setNavigationDirection(NavigationDirection.DOWN);
        },
        /** Sets currentItemIndex to the last item's index */
        focusLastItem: function () {
            setCurrentIndex(items.length - 1);
            setNavigationDirection(NavigationDirection.UP);
            var lastItem = items[items.length - 1];
            if (lastItem.items) {
                setGroupStartingItemIndex(lastItem.items.length - 1);
            }
        },
        clearFocus: function () {
            setCurrentIndex(null);
            setGroupStartingItemIndex(null);
        },
        /**
         * Stores the current navigation direction, use this to determine which side to enter a group from.
         * e.g. `navigationDirection === NavigationDirection.DOWN ? groupsFirstIndex : groupsLastIndex`
         */
        navigationDirection: navigationDirection,
        focusItemByPhrase: pushLetter,
    };
}
//# sourceMappingURL=hooks.js.map